<template>
    <div>
        <h3 class="content-block openware-form-title">{{ title }}</h3>

        <div class="content-block ow-import-actions">
            <DxButton
                icon="file"
                text="Επιλογή Αρχείου Πυρήνα"
                type="default"
                styling-mode="contained"
                :disabled="busy || importing"
                @click="onClickSelectPyrinas"
            />

            <DxButton
                icon="file"
                text="Επιλογή Αρχείου MyOffice"
                type="default"
                styling-mode="contained"
                :disabled="busy || importing"
                @click="onClickSelectMyOffice"
            />

            <input
                ref="filePyrinasRef"
                type="file"
                accept=".xlsx,.xls"
                style="display: none"
                @change="onPyrinasFileSelected"
            />

            <input
                ref="fileMyOfficeRef"
                type="file"
                accept=".xlsx,.xls"
                style="display: none"
                @change="onMyOfficeFileSelected"
            />
        </div>

        <DxLoadPanel
            :visible="busy"
            :show-pane="true"
            :show-indicator="true"
            shading-color="rgba(0,0,0,0.4)"
            message="Επεξεργασία αρχείου..."
        />

        <DxPopup
            v-model:visible="askDateVisible"
            :drag-enabled="false"
            :hide-on-outside-click="false"
            :show-close-button="true"
            :show-title="true"
            title="Ημερομηνία Ραντεβού (Πυρίνας)"
            :width="420"
            :height="220"
        >
            <div class="ow-date-prompt">
                <p>Επιλέξτε την ημερομηνία στην οποία αντιστοιχούν τα ραντεβού του αρχείου:</p>
                <DxDateBox
                    v-model:value="pyrinasDate"
                    type="date"
                    display-format="dd/MM/yyyy"
                    :width="200"
                />
                <div class="ow-date-prompt-buttons">
                    <DxButton text="Συνέχεια" type="success" @click="onConfirmPyrinasDate" />
                    <DxButton text="Ακύρωση" @click="askDateVisible = false" />
                </div>
            </div>
        </DxPopup>

        <div v-if="selection.source" class="content-block ow-import-panel">
            <div class="ow-import-info">
                <div class="ow-import-info-line">
                    <span class="ow-info-label">Αρχείο:</span>
                    <span>{{ selection.fileName }}</span>
                    <span class="ow-info-sep">|</span>
                    <span class="ow-info-label">Πηγή:</span>
                    <span>{{ selection.source === 'pyrinas' ? 'Πυρίνας' : 'MyOffice' }}</span>
                </div>

                <div v-if="selection.source === 'pyrinas'" class="ow-import-info-line">
                    <span class="ow-info-label">Ημερομηνία ραντεβού:</span>
                    <DxDateBox
                        v-model:value="pyrinasDate"
                        type="date"
                        display-format="dd/MM/yyyy"
                        :width="160"
                        :disabled="importing"
                        @value-changed="onPyrinasDateChange"
                    />
                </div>

                <div class="ow-import-info-line ow-import-counts">
                    <span class="ow-info-label">Σύνολο:</span> <b>{{ preview.total }}</b>
                    <span class="ow-info-sep">|</span>
                    <span class="ow-info-label">Έτοιμα:</span>
                    <span class="ow-tag-ok">{{ preview.matched }}</span>
                    <span class="ow-info-sep">|</span>
                    <span class="ow-info-label">Αγνοούνται:</span>
                    <span class="ow-tag-skip">{{ preview.skipped }}</span>
                    <span v-if="importResult.inserted || importResult.failed" class="ow-info-sep">|</span>
                    <template v-if="importResult.inserted || importResult.failed">
                        <span class="ow-info-label">Καταχωρήθηκαν:</span> <b>{{ importResult.inserted }}</b>
                        <span class="ow-info-sep">|</span>
                        <span class="ow-info-label">Αποτυχίες:</span> <b>{{ importResult.failed }}</b>
                    </template>
                </div>

                <div class="ow-import-actions-row">
                    <DxButton
                        icon="check"
                        text="Καταχώρηση Ραντεβού"
                        type="success"
                        styling-mode="contained"
                        :disabled="busy || importing || preview.matched === 0 || allImported"
                        @click="onClickRunImport"
                    />
                    <DxButton
                        icon="clear"
                        text="Καθαρισμός"
                        styling-mode="outlined"
                        :disabled="busy || importing"
                        @click="onClickReset"
                    />
                </div>
            </div>

            <DxDataGrid
                :data-source="preview.rows"
                key-expr="rowNo"
                :show-borders="true"
                :show-column-lines="true"
                :show-row-lines="true"
                :row-alternation-enabled="true"
                :column-auto-width="true"
                :word-wrap-enabled="true"
            >
                <DxPaging :enabled="true" :page-size="20" />
                <DxFilterRow :visible="true" />
                <DxSorting mode="multiple" />

                <DxColumn data-field="rowNo" caption="#" :width="60" />
                <DxColumn data-field="status" caption="Κατάσταση" :width="140" cell-template="statusTpl" />
                <DxColumn data-field="reason" caption="Σχόλιο / Λόγος" :width="260" />
                <DxColumn data-field="excelName" :caption="selection.source === 'pyrinas' ? 'Διαχειριστής (Excel)' : 'Όνομα (Excel)'" />
                <DxColumn data-field="appointmentDate" caption="Ημ/νία & Ώρα" :width="160" />
                <DxColumn data-field="durationMinutes" caption="Διάρκεια (λεπτά)" :width="120" :visible="selection.source === 'myoffice'" />
                <DxColumn data-field="excelAddress" caption="Διεύθυνση (Excel)" />
                <DxColumn data-field="excelPostal" caption="Τ.Κ. (Excel)" :width="100" :visible="selection.source === 'pyrinas'" />
                <DxColumn data-field="excelCity" caption="Πόλη (Excel)" :visible="selection.source === 'pyrinas'" />
                <DxColumn data-field="excelPhone" caption="Τηλέφωνο (Excel)" :visible="selection.source === 'pyrinas'" />
                <DxColumn data-field="excelAmount" caption="Αμοιβή (Excel)" :width="110" data-type="number" :visible="selection.source === 'pyrinas'" />
                <DxColumn data-field="excelComments" caption="Σχόλια (Excel)" />
                <DxColumn data-field="matchedClient" caption="Πελάτης (Σύστημα)" />
                <DxColumn data-field="workTypeId" caption="Είδος Εργ." :width="100" />
                <DxColumn data-field="workTotalPrice" caption="Κόστος" :width="100" data-type="number" />
                <DxColumn data-field="technicianMatched" caption="Τεχνικός (Σύστημα)" :visible="selection.source !== 'pyrinas'" />

                <template #statusTpl="{ data }">
                    <span :class="statusClass(data.value)">{{ statusLabel(data.value) }}</span>
                </template>
            </DxDataGrid>
        </div>
    </div>
</template>

<script setup>
    import { ref, computed } from 'vue'
    import { Workbook } from 'exceljs'

    import DxButton from 'devextreme-vue/button'
    import DxPopup from 'devextreme-vue/popup'
    import DxDateBox from 'devextreme-vue/date-box'
    import DxLoadPanel from 'devextreme-vue/load-panel'
    import {
        DxDataGrid,
        DxColumn,
        DxPaging,
        DxFilterRow,
        DxSorting,
    } from 'devextreme-vue/data-grid'

    import OParameters from '../es-globals'

    const title = ref('Εισαγωγή Ραντεβού από Excel')

    const URL_CLIENTS = OParameters.Host.URI + 'ow_clients'
    const URL_TECHNICIANS = OParameters.Host.URI + 'ow_view_technicians'
    const URL_APPOINTMENTS = OParameters.Host.URI + 'ow_appointments'

    const filePyrinasRef = ref(null)
    const fileMyOfficeRef = ref(null)

    const busy = ref(false)
    const importing = ref(false)

    const askDateVisible = ref(false)
    const pyrinasDate = ref(new Date())

    const selection = ref({ source: '', fileName: '' })
    const preview = ref({ total: 0, matched: 0, skipped: 0, rows: [] })
    const importResult = ref({ inserted: 0, failed: 0 })

    const lastFile = ref(null)
    const dataCache = ref({ clients: null, techniciansByName: null })

    const allImported = computed(() => {
        if (!preview.value.rows.length) return false
        return preview.value.rows.every((r) => r.status !== 'OK')
    })

    const statusLabel = (v) => {
        if (v === 'OK') return 'Έτοιμο'
        if (v === 'IMPORTED') return 'Καταχωρήθηκε'
        if (v === 'FAILED') return 'Αποτυχία'
        if (v === 'MULTI') return 'Πολλαπλοί'
        if (v === 'DUP') return 'Ήδη καταχ.'
        return 'Αγνοείται'
    }

    const statusClass = (v) => {
        if (v === 'OK') return 'ow-tag-ok'
        if (v === 'IMPORTED') return 'ow-tag-done'
        if (v === 'FAILED') return 'ow-tag-fail'
        if (v === 'MULTI') return 'ow-tag-multi'
        if (v === 'DUP') return 'ow-tag-dup'
        return 'ow-tag-skip'
    }

    const normalize = (s) => {
        if (s === null || s === undefined) return ''
        return String(s)
            .replace(/\s+/g, ' ')
            .trim()
            .toLocaleLowerCase('el-GR')
    }

    const normalizePhone = (s) => {
        if (s === null || s === undefined) return ''
        return String(s).replace(/\D/g, '')
    }

    const clientPhones = (c) => {
        return [c.phone_1, c.phone_2, c.mobile_phone_1, c.mobile_phone_2]
            .map(normalizePhone)
            .filter(Boolean)
    }

    const phonesMatch = (excelPhoneNorm, clientPhonesNorm) => {
        if (!excelPhoneNorm) return false
        return clientPhonesNorm.some((p) => p === excelPhoneNorm || p.endsWith(excelPhoneNorm) || excelPhoneNorm.endsWith(p))
    }

    const fetchJsonRecords = async (url) => {
        const response = await fetch(url, { method: 'GET', credentials: 'include' })
        const json = await response.json()
        return json.records || []
    }

    const postAppointment = async (payload) => {
        const response = await fetch(URL_APPOINTMENTS, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
        })
        if (!response.ok) {
            const text = await response.text()
            throw new Error(text || ('HTTP ' + response.status))
        }
        const text = await response.text()
        return text && JSON.parse(text)
    }

    const putClient = async (clientId, values) => {
        const response = await fetch(URL_CLIENTS + '/' + encodeURIComponent(clientId), {
            method: 'PUT',
            body: JSON.stringify(values),
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
        })
        if (!response.ok) {
            const text = await response.text()
            throw new Error(text || ('HTTP ' + response.status))
        }
        const text = await response.text()
        return text && JSON.parse(text)
    }

    const parsePyrinasPhones = (s) => {
        const text = String(s || '').trim()
        if (!text || text === '-') return { mobile: '', landline: '' }
        const parts = text.split(/[\s,;/]+|-/).map((p) => p.trim()).filter(Boolean)
        let mobile = ''
        let landline = ''
        for (const p of parts) {
            const digits = p.replace(/\D/g, '')
            if (!digits) continue
            if (digits.startsWith('69')) {
                if (!mobile) mobile = digits
            } else {
                if (!landline) landline = digits
            }
        }
        return { mobile, landline }
    }

    const fetchAppointmentsInRange = async (fromSql, toSql) => {
        const url = URL_APPOINTMENTS + '?filter=appointment_date,bt,' +
            encodeURIComponent(fromSql) + ',' + encodeURIComponent(toSql)
        return fetchJsonRecords(url)
    }

    const apptStartKey = (s) => {
        if (!s) return ''
        return String(s).replace('T', ' ').slice(0, 16)
    }

    const findDuplicateAppointment = (existing, clientId, apptDate) => {
        const key = toSqlDateTime(apptDate).slice(0, 16)
        return existing.find((a) =>
            Number(a.client_id) === Number(clientId) &&
            apptStartKey(a.appointment_date) === key
        )
    }

    // Πυρήνας: match on address (street) & Τ.Κ. only — city (πόλη) is intentionally ignored.
    const findClientsByAddress = (clients, excelStreet, excelPostal) => {
        const streetKey = normalize(excelStreet)
        const postalKey = normalize(excelPostal)
        if (!streetKey) return []
        return clients.filter((c) => {
            const cStreet = normalize(c.address_street)
            if (!cStreet) return false
            const streetOk = cStreet === streetKey || cStreet.includes(streetKey) || streetKey.includes(cStreet)
            if (!streetOk) return false
            const cPostal = normalize(c.address_postal_code)
            if (postalKey && cPostal && cPostal !== postalKey) return false
            return true
        })
    }

    const buildClientIndex = (clients) => {
        const byName = new Map()
        for (const c of clients) {
            const k = normalize(c.name)
            if (!k) continue
            if (!byName.has(k)) byName.set(k, [])
            byName.get(k).push(c)
        }
        return byName
    }

    const findClient = (clientsByName, excelName, excelAddress, excelCity, excelPostal, excelPhone) => {
        const key = normalize(excelName)
        if (!key) return { client: null, reason: 'Κενό όνομα πελάτη' }

        const matches = clientsByName.get(key)
        if (!matches || matches.length === 0) {
            return { client: null, reason: 'Ο πελάτης δεν βρέθηκε στο αρχείο πελατών' }
        }

        if (matches.length === 1) {
            const c = matches[0]
            if (!c.address_postal_code) {
                return { client: null, reason: 'Λείπει το Τ.Κ. στον πελάτη' }
            }
            return { client: c }
        }

        const addressKey = normalize(excelAddress)
        const cityKey = normalize(excelCity)
        const postalKey = normalize(excelPostal)
        const phoneKey = normalizePhone(excelPhone)

        if (!addressKey && !phoneKey && !postalKey) {
            return { client: null, reason: 'Πολλαπλοί πελάτες με ίδιο όνομα — κενή διεύθυνση/Τ.Κ./τηλέφωνο στο Excel' }
        }

        const candidates = matches.filter((c) => {
            if (!c.address_postal_code) return false

            const cStreet = normalize(c.address_street)
            const cCity = normalize(c.address_city)
            const cPostal = normalize(c.address_postal_code)

            const streetOk = addressKey && cStreet && (cStreet.includes(addressKey) || addressKey.includes(cStreet))
            const cityOk = !cityKey || !cCity || cCity.includes(cityKey) || cityKey.includes(cCity)
            const postalOk = !postalKey || cPostal === postalKey
            const addressMatch = streetOk && cityOk && postalOk

            const phoneMatch = phonesMatch(phoneKey, clientPhones(c))

            return addressMatch || phoneMatch
        })

        if (candidates.length === 0) {
            return { client: null, reason: 'Πολλαπλοί πελάτες με ίδιο όνομα — τηλέφωνο/διεύθυνση/Τ.Κ. δεν ταιριάζουν' }
        }
        if (candidates.length > 1) {
            return { client: null, reason: 'Πολλαπλοί πελάτες ταιριάζουν στην ίδια διεύθυνση/Τ.Κ./τηλέφωνο' }
        }
        return { client: candidates[0] }
    }

    const pad2 = (n) => String(n).padStart(2, '0')

    const toSqlDateTime = (d) => {
        return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate()) +
               ' ' + pad2(d.getHours()) + ':' + pad2(d.getMinutes()) + ':' + pad2(d.getSeconds())
    }

    const formatDt = (d) => {
        return pad2(d.getDate()) + '/' + pad2(d.getMonth() + 1) + '/' + d.getFullYear() +
               ' ' + pad2(d.getHours()) + ':' + pad2(d.getMinutes())
    }

    const cellText = (cell) => {
        if (cell === null || cell === undefined) return ''
        const v = cell.value !== undefined ? cell.value : cell
        if (v === null || v === undefined) return ''
        if (typeof v === 'object') {
            if (v.richText) return v.richText.map((r) => r.text).join('')
            if (v.text) return String(v.text)
            if (v instanceof Date) return v.toISOString()
            return ''
        }
        return String(v)
    }

    const cellTime = (cell) => {
        if (!cell) return null
        const v = cell.value
        if (v instanceof Date) {
            return { hours: v.getUTCHours(), minutes: v.getUTCMinutes() }
        }
        const s = cellText(cell).trim()
        const m = s.match(/(\d{1,2})[:.](\d{2})/)
        if (m) return { hours: parseInt(m[1], 10), minutes: parseInt(m[2], 10) }
        return null
    }

    const parseGreekDate = (s) => {
        const text = String(s || '').trim()
        const m = text.match(/(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})/)
        if (!m) return null
        const day = parseInt(m[1], 10)
        const month = parseInt(m[2], 10) - 1
        let year = parseInt(m[3], 10)
        if (year < 100) year += 2000
        return new Date(year, month, day)
    }

    const parseTimeRange = (s) => {
        const text = String(s || '').trim()
        const re = /(\d{1,2})[:.](\d{2})/g
        const times = []
        let m
        while ((m = re.exec(text)) !== null) {
            times.push({ hours: parseInt(m[1], 10), minutes: parseInt(m[2], 10) })
        }
        if (times.length === 0) return null
        const start = times[0]
        let durationMinutes = null
        if (times.length >= 2) {
            const end = times[1]
            const startMin = start.hours * 60 + start.minutes
            let endMin = end.hours * 60 + end.minutes
            if (endMin < startMin) endMin += 24 * 60
            durationMinutes = endMin - startMin
        }
        return { hours: start.hours, minutes: start.minutes, durationMinutes }
    }

    const buildTechnicianComment = (excelName, phones, comments) => {
        const parts = []
        if (excelName) parts.push(String(excelName).trim())
        const phoneList = (phones || []).map((p) => String(p || '').trim()).filter((p) => p && p !== '-')
        if (phoneList.length) parts.push('Τηλ.: ' + phoneList.join(', '))
        const cmt = String(comments || '').trim()
        if (cmt && cmt !== '-') parts.push(cmt)
        return parts.join(' | ')
    }

    const buildAppointmentPayload = (entry) => {
        const c = entry._client
        const d = entry._appointmentDate
        const totalPrice = entry._amountCredit != null ? entry._amountCredit : c.subscription_amount
        const payload = {
            locked_ind: 0,
            confirmed_ind: 1,
            work_completed_ind: 0,
            appointment_date: toSqlDateTime(d),
            client_id: c.id,
            appointment_address: [c.address_street, c.address_city, c.address_postal_code].filter(Boolean).join(' '),
            work_type_id: c.work_type_id,
            work_total_price: totalPrice,
            work_total_price_vat_ind: 0,
            vat_percent: 0,
            vat_value: 0.00,
            total_value: 0.00,
            collected_amount: 0.00,
            technician_comment: entry._technicianComment,
            date_time: toSqlDateTime(new Date()),
        }
        if (entry._technicianId) payload.technician = entry._technicianId
        if (entry._durationMinutes != null) payload.appointment_duration_minutes = entry._durationMinutes
        return payload
    }

    const buildClientUpdate = (entry) => {
        const updates = {}
        if (entry._managerName) updates.responsible_person_name1 = entry._managerName
        const phones = parsePyrinasPhones(entry._managerPhone)
        if (phones.mobile) updates.mobile_phone_1 = phones.mobile
        if (phones.landline) updates.phone_1 = phones.landline
        if (entry._amountCredit != null) updates.subscription_amount = entry._amountCredit
        return updates
    }

    const readWorkbook = async (file) => {
        const buffer = await file.arrayBuffer()
        const wb = new Workbook()
        await wb.xlsx.load(buffer)
        return wb.worksheets[0]
    }

    const findTechnicianInRow = (rowTexts, techniciansByName) => {
        const haystacks = rowTexts.map((t) => normalize(t)).filter(Boolean)
        for (const [key, tech] of techniciansByName.entries()) {
            if (!key) continue
            for (const h of haystacks) {
                if (h === key) return tech
            }
        }
        for (const [key, tech] of techniciansByName.entries()) {
            if (!key || key.length < 3) continue
            for (const h of haystacks) {
                if (h.includes(key)) return tech
            }
        }
        return null
    }

    const recomputePyrinasDates = async () => {
        if (selection.value.source !== 'pyrinas') return
        const baseDate = new Date(pyrinasDate.value)
        for (const entry of preview.value.rows) {
            if (entry._timeInfo) {
                const d = new Date(
                    baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(),
                    entry._timeInfo.hours, entry._timeInfo.minutes, 0,
                )
                entry._appointmentDate = d
                entry.appointmentDate = formatDt(d)
            }
        }

        busy.value = true
        try {
            const bounds = dayBoundsSql(baseDate)
            const existing = await fetchAppointmentsInRange(bounds.start, bounds.end)
            dataCache.value.existingAppointments = existing

            let matched = 0
            let skipped = 0
            for (const entry of preview.value.rows) {
                if (entry.status === 'IMPORTED' || entry.status === 'FAILED') {
                    if (entry.status === 'IMPORTED') matched++
                    else skipped++
                    continue
                }
                if (entry._client && entry._appointmentDate) {
                    const dup = findDuplicateAppointment(existing, entry._client.id, entry._appointmentDate)
                    if (dup) {
                        entry.status = 'DUP'
                        entry.reason = 'Έχει ήδη καταχωρηθεί ραντεβού (#' + (dup.id || '') + ')'
                        skipped++
                        continue
                    }
                    if (entry.status === 'DUP') {
                        entry.status = 'OK'
                        entry.reason = 'Έτοιμο προς καταχώρηση'
                    }
                }
                if (entry.status === 'OK') matched++
                else skipped++
            }
            preview.value.matched = matched
            preview.value.skipped = skipped
        } finally {
            busy.value = false
        }
    }

    const dayBoundsSql = (d) => {
        const start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0)
        const end = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59)
        return { start: toSqlDateTime(start), end: toSqlDateTime(end) }
    }

    const loadPyrinas = async (file, baseDate) => {
        busy.value = true
        try {
            const bounds = dayBoundsSql(baseDate)
            const [clients, existingAppointments, ws] = await Promise.all([
                fetchJsonRecords(URL_CLIENTS + '?filter=active_ind,eq,1'),
                fetchAppointmentsInRange(bounds.start, bounds.end),
                readWorkbook(file),
            ])
            dataCache.value.clients = clients
            dataCache.value.existingAppointments = existingAppointments

            const out = { total: 0, matched: 0, skipped: 0, rows: [] }

            for (let r = 5; r <= ws.rowCount; r++) {
                const row = ws.getRow(r)
                const fromInfo = cellTime(row.getCell(1))
                const toInfo = cellTime(row.getCell(2))
                const excelStreet = cellText(row.getCell(5)).trim()
                const excelPostal = cellText(row.getCell(6)).trim()
                const excelCity = cellText(row.getCell(7)).trim()
                const managerName = cellText(row.getCell(8)).trim()
                const amountCreditRaw = row.getCell(10).value
                const managerPhone = cellText(row.getCell(14)).trim()
                const workComments = cellText(row.getCell(16)).trim()

                if (!fromInfo || !toInfo || !excelStreet) continue

                out.total++

                const fullAddress = [excelStreet, excelCity, excelPostal].filter(Boolean).join(', ')
                let durationMinutes = null
                if (fromInfo && toInfo) {
                    const startMin = fromInfo.hours * 60 + fromInfo.minutes
                    let endMin = toInfo.hours * 60 + toInfo.minutes
                    if (endMin < startMin) endMin += 24 * 60
                    durationMinutes = endMin - startMin
                }

                let amountCredit = null
                if (amountCreditRaw !== null && amountCreditRaw !== undefined && amountCreditRaw !== '') {
                    if (typeof amountCreditRaw === 'object' && 'result' in amountCreditRaw) {
                        amountCredit = Number(amountCreditRaw.result)
                    } else {
                        amountCredit = Number(amountCreditRaw)
                    }
                    if (Number.isNaN(amountCredit)) amountCredit = null
                }

                const entry = {
                    rowNo: r,
                    excelName: managerName,
                    excelAddress: fullAddress,
                    excelPostal,
                    excelCity,
                    excelTechnician: '',
                    excelPhone: managerPhone,
                    excelComments: workComments,
                    excelAmount: amountCredit != null ? amountCredit : '',
                    appointmentDate: '',
                    matchedClient: '',
                    workTypeId: '',
                    workTotalPrice: amountCredit != null ? amountCredit : '',
                    technicianMatched: '',
                    status: 'SKIP',
                    reason: '',
                    _timeInfo: fromInfo,
                    _appointmentDate: null,
                    _durationMinutes: durationMinutes,
                    _client: null,
                    _technicianId: null,
                    _technicianComment: '',
                    _managerName: managerName,
                    _managerPhone: managerPhone,
                    _amountCredit: amountCredit,
                }

                const appointmentDate = new Date(
                    baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(),
                    fromInfo.hours, fromInfo.minutes, 0,
                )
                entry._appointmentDate = appointmentDate
                entry.appointmentDate = formatDt(appointmentDate)

                const matches = findClientsByAddress(clients, excelStreet, excelPostal)
                if (matches.length === 0) {
                    entry.reason = 'Δεν βρέθηκε πελάτης με αυτή τη διεύθυνση'
                    out.skipped++
                    out.rows.push(entry)
                    continue
                }
                if (matches.length > 1) {
                    entry.status = 'MULTI'
                    entry.reason = 'Πολλαπλοί πελάτες (' + matches.length + ') με ίδια διεύθυνση'
                    entry.matchedClient = matches.map((c) => c.name).join(' / ')
                    out.skipped++
                    out.rows.push(entry)
                    continue
                }

                const client = matches[0]
                entry._client = client
                entry._technicianComment = buildTechnicianComment(managerName, [managerPhone], workComments)
                entry.matchedClient = client.name
                entry.workTypeId = client.work_type_id
                if (amountCredit == null) entry.workTotalPrice = client.subscription_amount

                const dup = findDuplicateAppointment(existingAppointments, client.id, appointmentDate)
                if (dup) {
                    entry.status = 'DUP'
                    entry.reason = 'Έχει ήδη καταχωρηθεί ραντεβού (#' + (dup.id || '') + ')'
                    out.skipped++
                    out.rows.push(entry)
                    continue
                }

                entry.status = 'OK'
                entry.reason = 'Έτοιμο προς καταχώρηση'
                out.matched++
                out.rows.push(entry)
            }

            return out
        } finally {
            busy.value = false
        }
    }

    const loadMyOffice = async (file) => {
        busy.value = true
        try {
            const [clients, technicians, ws] = await Promise.all([
                fetchJsonRecords(URL_CLIENTS + '?filter=active_ind,eq,1'),
                fetchJsonRecords(URL_TECHNICIANS),
                readWorkbook(file),
            ])
            dataCache.value.clients = clients
            const clientsByName = buildClientIndex(clients)
            const techniciansByName = new Map()
            for (const t of technicians) {
                const k = normalize(t.name)
                if (k) techniciansByName.set(k, t)
            }
            dataCache.value.techniciansByName = techniciansByName

            const out = { total: 0, matched: 0, skipped: 0, rows: [] }

            for (let r = 3; r <= ws.rowCount; r++) {
                const row = ws.getRow(r)
                const dateCell = row.getCell(2)
                const timeCell = row.getCell(3)
                const excelName = cellText(row.getCell(4))
                if (!excelName.trim()) continue

                out.total++

                const excelStreet = cellText(row.getCell(5))
                const excelComments = cellText(row.getCell(6))

                const entry = {
                    rowNo: r,
                    excelName,
                    excelAddress: excelStreet,
                    excelPostal: '',
                    excelCity: '',
                    excelTechnician: '',
                    excelPhone: '',
                    excelComments,
                    appointmentDate: '',
                    durationMinutes: '',
                    matchedClient: '',
                    workTypeId: '',
                    workTotalPrice: '',
                    technicianMatched: '',
                    status: 'SKIP',
                    reason: '',
                    _timeInfo: null,
                    _appointmentDate: null,
                    _durationMinutes: null,
                    _client: null,
                    _technicianId: null,
                    _technicianComment: '',
                }

                let dateValue = null
                if (dateCell.value instanceof Date) {
                    const d = dateCell.value
                    dateValue = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
                } else {
                    dateValue = parseGreekDate(cellText(dateCell))
                }
                if (!dateValue) {
                    entry.reason = 'Μη έγκυρη ημερομηνία'
                    out.skipped++
                    out.rows.push(entry)
                    continue
                }

                const timeInfo = parseTimeRange(cellText(timeCell))
                if (!timeInfo) {
                    entry.reason = 'Μη έγκυρη ώρα'
                    out.skipped++
                    out.rows.push(entry)
                    continue
                }

                const appointmentDate = new Date(
                    dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate(),
                    timeInfo.hours, timeInfo.minutes, 0,
                )
                entry._timeInfo = timeInfo
                entry._appointmentDate = appointmentDate
                entry._durationMinutes = timeInfo.durationMinutes
                entry.appointmentDate = formatDt(appointmentDate)
                entry.durationMinutes = timeInfo.durationMinutes != null ? timeInfo.durationMinutes : ''

                const match = findClient(clientsByName, excelName, excelStreet, '', '', '')
                if (!match.client) {
                    entry.reason = match.reason
                    out.skipped++
                    out.rows.push(entry)
                    continue
                }

                const rowTexts = []
                row.eachCell({ includeEmpty: false }, (c) => { rowTexts.push(cellText(c)) })
                const tech = findTechnicianInRow(rowTexts, techniciansByName)

                entry._client = match.client
                entry._technicianId = tech ? tech.id : null
                entry._technicianComment = buildTechnicianComment(excelName, [], excelComments)
                entry.matchedClient = match.client.name
                entry.workTypeId = match.client.work_type_id
                entry.workTotalPrice = match.client.subscription_amount
                entry.technicianMatched = tech ? tech.name : ''
                entry.status = 'OK'
                entry.reason = 'Έτοιμο προς καταχώρηση'
                out.matched++
                out.rows.push(entry)
            }

            const okEntries = out.rows.filter((e) => e.status === 'OK' && e._appointmentDate)
            if (okEntries.length > 0) {
                let minDate = okEntries[0]._appointmentDate
                let maxDate = okEntries[0]._appointmentDate
                for (const e of okEntries) {
                    if (e._appointmentDate < minDate) minDate = e._appointmentDate
                    if (e._appointmentDate > maxDate) maxDate = e._appointmentDate
                }
                const fromBounds = dayBoundsSql(minDate)
                const toBounds = dayBoundsSql(maxDate)
                const existing = await fetchAppointmentsInRange(fromBounds.start, toBounds.end)
                dataCache.value.existingAppointments = existing
                for (const e of okEntries) {
                    const dup = findDuplicateAppointment(existing, e._client.id, e._appointmentDate)
                    if (dup) {
                        e.status = 'DUP'
                        e.reason = 'Έχει ήδη καταχωρηθεί ραντεβού (#' + (dup.id || '') + ')'
                        out.matched--
                        out.skipped++
                    }
                }
            }

            return out
        } finally {
            busy.value = false
        }
    }

    const onClickSelectPyrinas = () => {
        pyrinasDate.value = new Date()
        askDateVisible.value = true
    }

    const onConfirmPyrinasDate = () => {
        askDateVisible.value = false
        filePyrinasRef.value.value = ''
        filePyrinasRef.value.click()
    }

    const onPyrinasFileSelected = async (e) => {
        const file = e.target.files && e.target.files[0]
        if (!file) return
        lastFile.value = file
        const data = await loadPyrinas(file, new Date(pyrinasDate.value))
        selection.value = { source: 'pyrinas', fileName: file.name }
        importResult.value = { inserted: 0, failed: 0 }
        preview.value = data
    }

    const onPyrinasDateChange = () => {
        recomputePyrinasDates()
    }

    const onClickSelectMyOffice = () => {
        fileMyOfficeRef.value.value = ''
        fileMyOfficeRef.value.click()
    }

    const onMyOfficeFileSelected = async (e) => {
        const file = e.target.files && e.target.files[0]
        if (!file) return
        lastFile.value = file
        const data = await loadMyOffice(file)
        selection.value = { source: 'myoffice', fileName: file.name }
        importResult.value = { inserted: 0, failed: 0 }
        preview.value = data
    }

    const onClickRunImport = async () => {
        importing.value = true
        importResult.value = { inserted: 0, failed: 0 }
        const isPyrinas = selection.value.source === 'pyrinas'
        try {
            for (const entry of preview.value.rows) {
                if (entry.status !== 'OK') continue
                try {
                    const payload = buildAppointmentPayload(entry)
                    await postAppointment(payload)
                    if (isPyrinas && entry._client) {
                        const updates = buildClientUpdate(entry)
                        if (Object.keys(updates).length > 0) {
                            try {
                                await putClient(entry._client.id, updates)
                            } catch (clientErr) {
                                entry.reason = 'Καταχωρήθηκε (αποτυχία ενημέρωσης πελάτη: ' + (clientErr.message || clientErr) + ')'
                            }
                        }
                    }
                    entry.status = 'IMPORTED'
                    if (!entry.reason || entry.reason === 'Έτοιμο προς καταχώρηση') {
                        entry.reason = 'Καταχωρήθηκε'
                    }
                    importResult.value.inserted++
                } catch (err) {
                    entry.status = 'FAILED'
                    entry.reason = 'Σφάλμα καταχώρησης: ' + (err.message || err)
                    importResult.value.failed++
                }
            }
        } finally {
            importing.value = false
        }
    }

    const onClickReset = () => {
        selection.value = { source: '', fileName: '' }
        preview.value = { total: 0, matched: 0, skipped: 0, rows: [] }
        importResult.value = { inserted: 0, failed: 0 }
        lastFile.value = null
        dataCache.value = { clients: null, techniciansByName: null, existingAppointments: null }
    }
</script>


<style lang="scss" scoped>
    .ow-import-actions {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
        padding: 8px 30px;
    }

    .ow-date-prompt {
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    .ow-date-prompt-buttons {
        display: flex;
        gap: 8px;
        justify-content: flex-end;
    }

    .ow-import-panel {
        padding: 8px 30px 30px;
    }

    .ow-import-info {
        display: flex;
        flex-direction: column;
        gap: 8px;
        background: #f7f9fc;
        border: 1px solid #e0e6ee;
        border-radius: 6px;
        padding: 12px 16px;
        margin-bottom: 12px;
    }

    .ow-import-info-line {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;
    }

    .ow-import-counts {
        font-size: 14px;
    }

    .ow-info-label {
        font-weight: 600;
        color: #4a5568;
    }

    .ow-info-sep {
        color: #cbd5e0;
        padding: 0 4px;
    }

    .ow-import-actions-row {
        display: flex;
        gap: 8px;
        margin-top: 4px;
    }

    .ow-tag-ok,
    .ow-tag-skip,
    .ow-tag-done,
    .ow-tag-fail,
    .ow-tag-multi,
    .ow-tag-dup {
        display: inline-block;
        padding: 2px 8px;
        border-radius: 10px;
        font-weight: 600;
        font-size: 12px;
    }

    .ow-tag-ok {
        background: #d4edda;
        color: #155724;
    }

    .ow-tag-skip {
        background: #f8d7da;
        color: #721c24;
    }

    .ow-tag-done {
        background: #cce5ff;
        color: #004085;
    }

    .ow-tag-fail {
        background: #fff3cd;
        color: #856404;
    }

    .ow-tag-multi {
        background: #ffe5b4;
        color: #8a4b00;
    }

    .ow-tag-dup {
        background: #e2d4f0;
        color: #4b2980;
    }
</style>
